import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, abs, errMsg } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { AdminTitle, Pill, ErrorBox } from "./AdminLayout";

const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const MEDIA_COLS = [["thumb", ""], ["file", "File"], ["type", "Type"], ["size", "Size"], ["used", "Used by"], ["actions", ""]];

function useMedia(kind, unused) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/media", { params: { kind: kind || undefined, unused: unused || undefined } })
    .then((r) => { setItems(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [kind, unused]);
  useEffect(() => { load(); }, [load]);
  return { items, err, load };
}

const MediaFilters = ({ kind, setKind, unused, setUnused }) => (
  <>
    <select className="wb-input !w-40" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="media-kind-filter">
      <option value="">All types</option><option value="image">Images</option><option value="audio">Audio</option><option value="video">Video</option><option value="other">Other</option>
    </select>
    <label className="flex items-center gap-2 text-xs muted"><input type="checkbox" className="accent-[#D4AF37]" checked={unused} onChange={(e) => setUnused(e.target.checked)} data-testid="media-unused-filter" /> Unused only</label>
  </>
);

const UsedBy = ({ used }) => (used.length
  ? used.map((u) => <div key={`${u.where}-${u.field}-${u.product_id || ""}`}>{u.where} <span className="muted">· {u.field}</span></div>)
  : <Pill>Unused</Pill>);

function MediaRow({ m, onDelete }) {
  const inUse = m.used_by.length > 0;
  return (
    <tr className="border-b wb-line hover:bg-wb-card" data-testid={`media-row-${m.id}`}>
      <td className="px-3 py-2">{m.content_type.startsWith("image/") && m.url ? <img src={abs(m.url)} alt="" className="h-10 w-10 object-cover" /> : <div className="h-10 w-10 bg-wb-bg2" />}</td>
      <td className="px-3 py-2"><div className="max-w-[260px] truncate">{m.filename}</div><div className="text-xs muted">{m.public ? "Public" : "Private (buyers only)"}{m.watermark && " · watermarked"}{m.parent_id && " · edited version"}</div></td>
      <td className="px-3 py-2 font-mono text-xs muted">{m.content_type}</td>
      <td className="px-3 py-2 font-mono text-xs">{size(m.size)}</td>
      <td className="px-3 py-2 text-xs"><UsedBy used={m.used_by} /></td>
      <td className="px-3 py-2 text-right">
        <button onClick={() => onDelete(m)} disabled={inUse} title={inUse ? "In use — remove it from products/site first" : "Delete"} className="p-2 muted hover:text-red-400 disabled:opacity-30" data-testid="media-delete-button"><Trash2 size={15} /></button>
      </td>
    </tr>
  );
}

const MediaTable = ({ items, onDelete }) => (
  <div className="overflow-x-auto border wb-line">
    <table className="w-full text-sm">
      <thead><tr className="border-b wb-line text-left">{MEDIA_COLS.map(([k, h]) => <th key={k} className="px-3 py-3 font-mono text-[0.62rem] uppercase tracking-[0.2em] font-normal muted">{h}</th>)}</tr></thead>
      <tbody>{items.map((m) => <MediaRow key={m.id} m={m} onDelete={onDelete} />)}</tbody>
    </table>
  </div>
);

export default function MediaLibrary() {
  const [kind, setKind] = useState("");
  const [unused, setUnused] = useState(false);
  const { items, err, load } = useMedia(kind, unused);
  const del = async (m) => {
    if (!window.confirm(`Delete ${m.filename}? This cannot be undone.`)) return;
    try { const { data } = await api.delete(`/admin/media/${m.id}`); toast.success(`Deleted (${data.watermark_copies_removed} watermark copies cleaned)`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-media">
      <AdminTitle eyebrow="Files" title="Media library">
        <MediaFilters kind={kind} setKind={setKind} unused={unused} setUnused={setUnused} />
      </AdminTitle>
      {err ? <ErrorBox msg={err} /> : !items ? <Loading /> : items.length === 0 ? <p className="py-10 muted" data-testid="media-empty">No files.</p> : <MediaTable items={items} onDelete={del} />}
    </div>
  );
}
